/**
 * SlideOverForm — Drawer lateral moderno (estilo Odoo/Linear/Notion).
 *
 * Reemplaza los modales centrados tipo "ventana emergente". Se abre desde la
 * derecha con animación smooth, mantiene el contexto del listado visible
 * atrás bajo un backdrop de blur sutil, y presenta los formularios como una
 * experiencia contextual (no un pop-up bloqueante).
 *
 * Uso:
 *   <SlideOverForm title="Editar orden · OT-1234"
 *                  breadcrumb="Órdenes › OT-1234 › Editar"
 *                  subtitle={order.client_name}
 *                  onClose={() => setEditing(false)}
 *                  width="720px"
 *                  footerActions={<><button>Guardar</button><button>Cancelar</button></>}>
 *      <form>...</form>
 *   </SlideOverForm>
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ArrowLeft } from "lucide-react";

export default function SlideOverForm({
  title,
  subtitle,
  breadcrumb,
  onClose,
  children,
  width = "720px",
  headerRight,
  footerActions,
  testId,
  accent = "#0A3747",
  accentSecondary = "#E0E621",
}) {
  const [mounted, setMounted] = useState(false);

  // Trigger enter animation after mount
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 10);
    return () => clearTimeout(t);
  }, []);

  // ESC to close + lock body scroll
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") handleClose(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line
  }, []);

  const handleClose = () => {
    setMounted(false);
    // Wait for exit animation
    setTimeout(() => onClose?.(), 260);
  };

  const content = (
    <div className="fixed inset-0 z-[10000] flex justify-end" data-testid={testId || "slideover-form"}>
      {/* Backdrop translúcido con blur */}
      <button
        aria-label="Cerrar"
        onClick={handleClose}
        className={`absolute inset-0 bg-slate-900/30 backdrop-blur-[3px] transition-opacity duration-300 cursor-default ${mounted ? "opacity-100" : "opacity-0"}`}
        data-testid="slideover-backdrop"
      />

      {/* Panel */}
      <div
        className={`relative h-full bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] ${mounted ? "translate-x-0" : "translate-x-full"}`}
        style={{ width: "100%", maxWidth: width, borderLeft: `4px solid ${accentSecondary}` }}
        data-testid="slideover-panel"
      >
        {/* Header */}
        <div
          className="flex-shrink-0 border-b border-slate-200/70 bg-gradient-to-br px-5 py-3.5 relative overflow-hidden"
          style={{
            backgroundImage: `linear-gradient(135deg, ${accent} 0%, ${accent}dd 100%)`,
          }}
        >
          {/* Textura sutil */}
          <div
            className="absolute inset-0 opacity-[0.06] pointer-events-none"
            style={{
              backgroundImage: `radial-gradient(circle at 20% 50%, ${accentSecondary} 0%, transparent 40%)`,
            }}
          />
          <div className="relative flex items-start gap-3">
            <button
              onClick={handleClose}
              className="flex-shrink-0 mt-0.5 p-1.5 rounded-md hover:bg-white/10 text-white/90 hover:text-white transition-all group"
              aria-label="Cerrar"
              data-testid="slideover-back"
            >
              <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
            </button>
            <div className="flex-1 min-w-0">
              {breadcrumb && (
                <div
                  className="text-[9.5px] font-mono uppercase tracking-[0.22em] truncate mb-1"
                  style={{ color: accentSecondary, opacity: 0.85 }}
                >
                  {breadcrumb}
                </div>
              )}
              <h2
                className="text-[15px] font-bold text-white truncate leading-tight"
                style={{ fontFamily: "Space Grotesk, Chivo, sans-serif" }}
                data-testid="slideover-title"
              >
                {title}
              </h2>
              {subtitle && (
                <div className="text-[11px] text-white/70 mt-0.5 truncate">{subtitle}</div>
              )}
            </div>
            {headerRight}
            <button
              onClick={handleClose}
              className="flex-shrink-0 p-1.5 rounded-md hover:bg-white/10 text-white/80 hover:text-white transition-all -mr-1"
              aria-label="Cerrar"
              data-testid="slideover-close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Body scrollable */}
        <div className="flex-1 overflow-y-auto bg-slate-50/40" data-testid="slideover-body">
          <div className="px-5 py-4">{children}</div>
        </div>

        {/* Footer sticky con acciones */}
        {footerActions && (
          <div
            className="flex-shrink-0 sticky bottom-0 z-10 border-t border-slate-200 bg-white px-5 py-3 flex items-center gap-2 shadow-[0_-4px_16px_rgba(0,0,0,0.04)]"
            data-testid="slideover-footer"
          >
            {footerActions}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

/**
 * SlideOverSection — bloque tipo card para agrupar campos dentro del drawer.
 */
export function SlideOverSection({ title, icon: Icon, children, description, accent = "#0A3747", testId }) {
  return (
    <section className="bg-white rounded-lg border border-slate-200 shadow-sm mb-3 overflow-hidden" data-testid={testId}>
      <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2">
        {Icon && (
          <div
            className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
            style={{ background: `${accent}12`, color: accent }}
          >
            <Icon size={12} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] font-mono uppercase tracking-widest font-semibold"
            style={{ color: accent }}
          >
            {title}
          </div>
          {description && (
            <div className="text-[10px] text-slate-500 mt-0.5 truncate">{description}</div>
          )}
        </div>
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}
